import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Chat from "@/components/Chat";
import FavoritosButton from "@/components/FavoritosButton";
import BookingModal, { BookingData } from "@/components/BookingModal";
import AvailableDatesCard from "@/components/AvailableDatesCard";
import SocialShare from "@/components/SocialShare";
import { useAuthState } from "@/hooks/useAuthState";
import { getPropertyImageUrl, getUserImageUrl } from "@/config/api";
import { getImageUrl } from "@/lib/imageMap";
import { buildPropertyWhatsAppMessage, buildWhatsAppUrl } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  MapPin,
  Star,
  Users,
  Bed,
  Wifi,
  Waves,
  Car,
  Utensils,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  AlertCircle,
  ArrowLeft,
  ParkingCircle,
  Thermometer,
  Tv,
  Heart,
  Dumbbell,
  Building2,
  BadgeCheck,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMessages } from "@/hooks/useMessages";
import { BookingService } from "@/services/bookingService";
import { AvailabilityService } from "@/services/availabilityService";
import { PropertyService } from "@/services/propertyService";
import { PropertyPhotoService } from "@/services/propertyPhotoService";
import { UserService } from "@/services/userService";
import { DevelopmentService } from "@/services/developmentService";
import { developmentHref } from "@/lib/developmentLinks";
import { Property, PropertyPhoto } from "@/integrations/mysql/types";
import { User } from "@/services/authService";
import apartment1 from "@/assets/apartment-1.jpg";
import house1 from "@/assets/house-1.jpg";
import heroBeach from "@/assets/hero-beach.jpg";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";
import piscinasNaturais from "@/assets/piscinas-naturais.jpg";
import portoGalinhasTown from "@/assets/porto-galinhas-town.jpg";
import resort1 from "@/assets/resort-1.jpg";
import resortInfrastructure from "@/assets/resort-infrastructure.jpg";
import cottage1 from "@/assets/cottage-1.jpg";
import penthouse1 from "@/assets/penthouse-1.jpg";

type PropertyDetailsProps = {
  /** Página /imovel-venda — cota à venda, só contato com proprietário */
  saleMode?: boolean;
};

const PropertyDetails = ({ saleMode = false }: PropertyDetailsProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showImageModal, setShowImageModal] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [hasAvailablePeriods, setHasAvailablePeriods] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(true);
  const [propertyNotFound, setPropertyNotFound] = useState(false);
  const [property, setProperty] = useState<Property | null>(null);
  const [propertyPhotos, setPropertyPhotos] = useState<PropertyPhoto[]>([]);
  const [loadingProperty, setLoadingProperty] = useState(true);
  const [owner, setOwner] = useState<User | null>(null);
  const [loadingOwner, setLoadingOwner] = useState(false);
  const [developmentData, setDevelopmentData] = useState<any | null>(null);
  const {
    isAuthenticated,
    userType,
    userName,
    userPhoto,
    userId,
    subscriptionPlan,
    clearSession,
  } = useAuthState();
  const { getOrCreateConversation } = useMessages();

  /** Telefone/e-mail do proprietário: admin, Live ou Porto2Life. Demais usuários usam o chat. */
  const canSeeOwnerContactDetails =
    userType === "admin" ||
    subscriptionPlan === "live" ||
    subscriptionPlan === "porto2life";

  // Capturar filtros da URL (vindos da busca)
  const searchFilters = {
    dataInicio: searchParams.get('inicio') || undefined,
    dataFim: searchParams.get('fim') || undefined,
    hospedes: searchParams.get('hospedes') ? parseInt(searchParams.get('hospedes')!) : undefined,
  };

  // Verificar se o usuário é o proprietário deste imóvel
  const isOwnerOfThisProperty = userType === 'owner' && property && userId === property.owner_id;

  // Validação de sessão - detectar sessões inválidas
  useEffect(() => {
    const validateSession = () => {
      // Verificar se há dados inconsistentes no localStorage
      const token = localStorage.getItem('authToken');
      const userStr = localStorage.getItem('user');
      const isAuth = localStorage.getItem('isAuthenticated');
      const userTypeStorage = localStorage.getItem('userType');
      const userIdStorage = localStorage.getItem('userId');

      // Se há dados parciais ou inconsistentes, limpar tudo
      const hasPartialData = (token && !userStr) || 
                            (userStr && !token) || 
                            (isAuth === 'true' && !userTypeStorage) ||
                            (userTypeStorage && !userIdStorage);

      if (hasPartialData) {
        console.log('🔍 Sessão inconsistente detectada, limpando...');
        clearSession();
      }
    };

    validateSession();
  }, [clearSession]);

  // Função para carregar dados do proprietário
  const loadOwnerData = async (ownerId: number) => {
    try {
      setLoadingOwner(true);
      console.log('👤 Carregando dados do proprietário ID:', ownerId);

      const ownerData = await UserService.getUserById(ownerId);

      if (!ownerData) {
        console.log('❌ Proprietário não encontrado');
        setOwner(null);
        return;
      }

      const maySeeContact =
        userType === 'admin' ||
        subscriptionPlan === 'live' ||
        subscriptionPlan === 'porto2life';
      if (maySeeContact) {
        const contact = await UserService.getOwnerContactDetails(ownerId);
        if (contact) {
          ownerData.phone = contact.phone ?? undefined;
          ownerData.email = contact.email ?? undefined;
          if (contact.phone_ddi) {
            ownerData.phone_ddi = contact.phone_ddi;
          }
        }
      }

      console.log('✅ Proprietário carregado:', ownerData);
      setOwner(ownerData);
    } catch (error) {
      console.error('❌ Erro ao carregar proprietário:', error);
      setOwner(null);
    } finally {
      setLoadingOwner(false);
    }
  };

  // Carregar dados da propriedade do banco
  useEffect(() => {
    const loadProperty = async () => {
      if (!id) {
        console.log('❌ ID não fornecido');
        return;
      }
      
      try {
        setLoadingProperty(true);
        setPropertyNotFound(false);
        setProperty(null);
        console.log('🏠 Carregando propriedade ID:', id);
        console.log('🔢 ID convertido para número:', Number(id));
        
        const propertyData = saleMode
          ? await PropertyService.getPropertyByIdAnyStatus(Number(id))
          : await PropertyService.getPropertyByIdSimple(Number(id));
        
        if (propertyData) {
          const forSale =
            propertyData.is_share_for_sale === true ||
            propertyData.is_share_for_sale === 1;
          if (saleMode && !forSale) {
            setPropertyNotFound(true);
            setLoadingProperty(false);
            return;
          }
          console.log('✅ Propriedade carregada:', propertyData);
          setProperty(propertyData);
          
          // Carregar dados do proprietário
          await loadOwnerData(propertyData.owner_id);
          
          // Carregar dados do empreendimento se houver development_id
          if (propertyData.development_id) {
            try {
              const development = await DevelopmentService.getDevelopmentById(propertyData.development_id);
              if (development) {
                setDevelopmentData(development);
              }
            } catch (error) {
              console.error('❌ Erro ao carregar empreendimento:', error);
            }
          }
          
          // Carregar fotos do imóvel
          try {
            const photos = await PropertyPhotoService.getActivePhotosByPropertyId(Number(id));
            console.log('📸 Fotos carregadas:', photos);
            setPropertyPhotos(photos);
          } catch (photoError) {
            console.error('❌ Erro ao carregar fotos:', photoError);
            setPropertyPhotos([]);
          }
        } else {
          console.log('❌ Propriedade não encontrada ou inativa');
          console.log('🔍 Verificando se o imóvel existe mas está inativo...');
          
          // Verificar se o imóvel existe mas está inativo
          try {
            const inactiveProperty = await PropertyService.getPropertyByIdAnyStatus(Number(id));
            if (inactiveProperty) {
              console.log('⚠️ Imóvel encontrado mas está INATIVO:', inactiveProperty.title);
            }
          } catch (error) {
            console.log('❌ Imóvel não existe no banco de dados');
          }
          
          setPropertyNotFound(true);
        }
      } catch (error) {
        console.error('❌ Erro ao carregar propriedade:', error);
        setPropertyNotFound(true);
      } finally {
        setLoadingProperty(false);
      }
    };

    loadProperty();
  }, [id, saleMode]);

  useEffect(() => {
    if (!property?.owner_id || !owner) return;
    void loadOwnerData(property.owner_id);
  }, [userType, subscriptionPlan]);

  const handleContactOwner = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Login necessário",
        description: "Você precisa estar logado para entrar em contato com o proprietário.",
      });
      navigate("/login");
      return;
    }

    if (userType === 'owner' && property && userId === property.owner_id) {
      toast({
        title: "Acesso restrito",
        description: "Você não pode entrar em contato consigo mesmo.",
      });
      return;
    }

    try {
      setLoading(true);
      
      if (!property) {
        toast({
          title: "Erro",
          description: "Dados da propriedade não carregados.",
          variant: "destructive",
        });
        return;
      }

      console.log('🚀 Criando/obtendo conversa:', {
        propertyId: Number(id),
        guestId: userId,
        ownerId: property.owner_id
      });
      
      // Criar ou obter conversa
      const conversation = await getOrCreateConversation(
        Number(id),
        userId!,
        property.owner_id
      );
      
      console.log('📋 Conversa retornada:', conversation);
      
      if (conversation) {
        console.log('✅ Definindo conversationId:', conversation.id.toString());
        setConversationId(conversation.id.toString());
        toast({
          title: "Conversa iniciada",
          description: "Chat aberto com o proprietário.",
        });
      } else {
        console.error('❌ Conversa não foi criada/obtida');
        toast({
          title: "Erro",
          description: "Não foi possível iniciar a conversa.",
          variant: "destructive",
        });
      }
      
    } catch (error) {
      console.error("❌ Error creating conversation:", error);
      toast({
        title: "Erro",
        description: "Não foi possível iniciar a conversa.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Dados da propriedade agora vêm do banco de dados via estado 'property'

  // Verificar se o imóvel tem períodos disponíveis
  useEffect(() => {
    if (saleMode) {
      setCheckingAvailability(false);
      setHasAvailablePeriods(false);
      return;
    }
    const checkPropertyAvailability = async () => {
      if (id && property && !propertyNotFound) {
        try {
          setCheckingAvailability(true);
          
          // Verificar períodos disponíveis para o imóvel
          const periods = await AvailabilityService.getAvailabilityPeriods(parseInt(id));
          setHasAvailablePeriods(periods.length > 0);
        } catch (error) {
          console.error('Erro ao verificar disponibilidade:', error);
          setHasAvailablePeriods(false);
        } finally {
          setCheckingAvailability(false);
        }
      } else {
        // Se não há propriedade ou está not found, não verificar disponibilidade
        setCheckingAvailability(false);
        setHasAvailablePeriods(false);
      }
    };

    checkPropertyAvailability();
  }, [id, property, propertyNotFound, saleMode]);

  const nextImage = () => {
    const maxIndex = Math.max(0, propertyPhotos.length - 1);
    setCurrentImageIndex((prev) => (prev + 1) % Math.max(1, propertyPhotos.length));
  };

  const prevImage = () => {
    const maxIndex = Math.max(0, propertyPhotos.length - 1);
    setCurrentImageIndex((prev) => prev === 0 ? maxIndex : prev - 1);
  };

  // Navegação por teclado
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);


  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Login necessário",
        description: "Faça login para fazer uma reserva",
        variant: "destructive",
      });
      navigate("/login");
      return;
    }

    if (userType === 'admin') {
      toast({
        title: "Acesso restrito",
        description: "Usuários administradores não podem fazer reservas. Use a opção 'Falar com Proprietário' para contato.",
      });
      return;
    }

    if (userType === 'owner' && property && userId === property.owner_id) {
      toast({
        title: "Acesso restrito",
        description: "Você não pode reservar seu próprio imóvel.",
      });
      return;
    }

    setShowBookingModal(true);
  };

  const handleConfirmBooking = async (bookingData: BookingData) => {
    console.log("🔍 Estado de autenticação:", { isAuthenticated, userId, userName, userType });
    
    if (!isAuthenticated || !userId) {
      console.error("❌ User ID não encontrado ou usuário não autenticado");
      toast({
        title: "Login Necessário",
        description: "Você precisa estar logado para fazer uma reserva. Redirecionando para login...",
        variant: "destructive",
      });
      setTimeout(() => {
        navigate(`/login?redirect=/imovel/${id}`);
      }, 2000);
      return;
    }

    if (userType === 'admin') {
      console.error("❌ Usuário admin tentando fazer reserva");
      toast({
        title: "Acesso Restrito",
        description: "Usuários administradores não podem fazer reservas.",
        variant: "destructive",
      });
      return;
    }

    if (!property) {
      console.error("❌ Property não carregada");
      toast({
        title: "Erro",
        description: "Dados da propriedade não carregados.",
        variant: "destructive",
      });
      return;
    }

    console.log("🚀 Iniciando solicitação de reserva...");
    console.log("👤 User ID:", userId);
    console.log("🏠 Property ID:", property.id);
    console.log("📅 Booking Data:", bookingData);

    try {
      setBookingLoading(true);

      const bookingRequestData = {
        property_id: property.id,
        guest_id: userId,
        availability_period_id: bookingData.availabilityPeriodId,
        check_in: bookingData.checkIn,
        check_out: bookingData.checkOut,
        total_nights: bookingData.totalNights,
        total_price: bookingData.totalPrice,
        guest_notes: bookingData.guestNotes,
      };

      console.log("📝 Dados da solicitação:", bookingRequestData);

      // Verificação adicional no frontend antes de enviar
      const existingCheck = await BookingService.hasPendingBookingForPeriod(userId, bookingData.availabilityPeriodId);
      if (existingCheck) {
        toast({
          title: "Reserva já existe",
          description: "Você já possui uma solicitação pendente para este período. Aguarde a resposta do proprietário.",
          variant: "destructive",
        });
        setShowBookingModal(false);
        return;
      }

      const bookingRequest = await BookingService.createBookingRequest(bookingRequestData);

      console.log("✅ Solicitação criada com sucesso:", bookingRequest);

      toast({
        title: "Solicitação enviada!",
        description: "Sua solicitação de reserva foi enviada. O proprietário tem 24 horas para entrar em contato.",
      });

      setShowBookingModal(false);
      
      // Recarregar a página ou atualizar os dados após criar a reserva
      window.location.reload();
    } catch (error) {
      console.error("❌ Erro ao criar solicitação de reserva:", error);
      const errorMessage = error instanceof Error ? error.message : "Não foi possível enviar a solicitação de reserva.";
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setBookingLoading(false);
    }
  };

  const parseDevelopmentPhotos = (): string[] => {
    if (!developmentData?.photos) return [];
    if (Array.isArray(developmentData.photos)) return developmentData.photos;
    if (typeof developmentData.photos === 'string') {
      try {
        return JSON.parse(developmentData.photos);
      } catch {
        return [];
      }
    }
    return [];
  };

  const mergedHouseRules = [developmentData?.house_rules, property?.house_rules]
    .filter((item) => typeof item === 'string' && item.trim() !== '')
    .join('<hr class="my-3" />');

  const mergedAdditionalInfo = [developmentData?.additional_info, property?.additional_info]
    .filter((item) => typeof item === 'string' && item.trim() !== '')
    .join('<hr class="my-3" />');

  return (
    <div className="min-h-screen bg-background">
      <Navbar 
        isAuthenticated={isAuthenticated} 
        isOwner={userType === "owner"} 
        userName={userName}
        userPhoto={userPhoto}
        userId={userId}
        userType={userType}
      />

      <div className="container mx-auto px-4 py-8">
        {/* Verificar se o imóvel foi encontrado */}
        {loadingProperty ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Carregando imóvel...</p>
            </div>
          </div>
        ) : propertyNotFound || !property ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center max-w-md">
              <AlertCircle className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Imóvel não encontrado</h1>
              <p className="text-muted-foreground mb-6">
                {saleMode
                  ? "Esta cota não está disponível para venda ou o imóvel não foi encontrado."
                  : "Este imóvel não está disponível no momento. Pode ter sido removido ou está temporariamente indisponível."}
              </p>
              <Button onClick={() => navigate(saleMode ? "/venda" : "/")} className="gap-2">
                <ArrowLeft className="h-4 w-4" />
                Voltar para a página inicial
              </Button>
            </div>
          </div>
        ) : !saleMode && checkingAvailability ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Verificando disponibilidade do imóvel...</p>
            </div>
          </div>
        ) : (
          <div>
            {/* Galeria de Fotos */}
        <div className="mb-8">
          <div className="relative h-[500px] rounded-2xl overflow-hidden shadow-large">
            <img
              src={
                propertyPhotos.length > 0 && property
                  ? getPropertyImageUrl(property.id, propertyPhotos[currentImageIndex]?.filename)
                  : apartment1
              }
              alt={property ? `${property.title} - Foto ${currentImageIndex + 1}` : "Imóvel"}
              className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => property && setShowImageModal(true)}
            />

            {/* Badge de Verificado */}
            {Number(property?.is_verified) === 1 && (
              <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1.5 rounded-full flex items-center gap-1.5 text-sm font-semibold shadow-lg z-10">
                <BadgeCheck className="h-4 w-4" />
                Verificado
              </div>
            )}

            {/* Botão de favoritos - apenas para usuários logados */}
            {isAuthenticated && property && !saleMode && (
              <FavoritosButton
                propertyId={property.id}
                propertyTitle={property.title}
                propertyImage={apartment1}
                propertyPrice={undefined}
                propertyLocation={property.city}
              />
            )}

            <Button
              variant="secondary"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg"
              onClick={prevImage}
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>

            <Button
              variant="secondary"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg"
              onClick={nextImage}
            >
              <ChevronRight className="h-6 w-6" />
            </Button>

            {/* Contador de imagens - acima da legenda */}
            <div className="absolute bottom-16 right-4 bg-black/70 text-white px-3 py-2 rounded-lg text-sm font-medium backdrop-blur-sm">
              {currentImageIndex + 1} / {Math.max(1, propertyPhotos.length)}
            </div>

            {/* Paginação - acima da legenda */}
            <div className="absolute bottom-16 left-1/2 -translate-x-1/2 flex gap-2 max-w-[80%] overflow-x-auto scrollbar-hide">
              {Array.from({ length: Math.max(1, propertyPhotos.length) }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`h-2 rounded-full transition-all flex-shrink-0 w-8 ${
                    index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>

            {/* Legenda da imagem - cobre 100% da parte inferior */}
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white px-4 py-3 text-sm backdrop-blur-sm">
              {propertyPhotos.length > 0 && propertyPhotos[currentImageIndex]?.caption 
                ? propertyPhotos[currentImageIndex].caption 
                : property?.title || "Imóvel"
              }
            </div>
          </div>

          {/* Thumbs da Galeria */}
          {propertyPhotos.length > 1 && (
            <div className="mt-4">
              <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
                {propertyPhotos.map((photo, index) => (
                  <button
                    key={photo.id}
                    onClick={() => {
                      setCurrentImageIndex(index);
                      if (property) {
                        setShowImageModal(true);
                      }
                    }}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer hover:opacity-90 ${
                      index === currentImageIndex 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img
                      src={
                        property
                          ? getImageUrl(photo.filename, property.id, true)
                          : apartment1
                      }
                      alt={`Thumb ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      sizes="80px"
                      onError={(e) => {
                        const img = e.currentTarget;
                        if (property && img.src.includes("/thumb/")) {
                          img.src = getPropertyImageUrl(property.id, photo.filename);
                        }
                      }}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Informações Principais */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-bold text-foreground">
                      {property.title}
                    </h1>
                    {Number(property.is_verified) === 1 && (
                      <div className="bg-blue-500 text-white px-3 py-1 rounded-full flex items-center gap-1.5 text-sm font-semibold">
                        <BadgeCheck className="h-4 w-4" />
                        Verificado
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="text-base font-medium text-primary">
                      {developmentData?.name || "Resort não informado"}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{developmentData?.city || property.city} - {developmentData?.state || property.state}</span>
                  </div>
                </div>
                <div className="ml-4">
                  <SocialShare
                    url={saleMode ? `/imovel-venda/${property.id}` : `/imovel/${property.id}`}
                    title={property.title}
                    description={property.description}
                    type="property"
                    resort={
                      developmentData
                        ? {
                            name: developmentData.name,
                            city: developmentData.city || property.city,
                            state: developmentData.state || property.state,
                            description: developmentData.description,
                          }
                        : {
                            city: property.city,
                            state: property.state,
                          }
                    }
                  />
                </div>
              </div>

              <div className="flex gap-6 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Bed className="h-5 w-5" />
                  {property.bedrooms} quartos
                </div>
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  {property.max_guests} hóspedes
                </div>
              </div>
            </div>


            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-4">Sobre o imóvel</h2>
              {property.description ? (
                <div 
                  className="text-muted-foreground leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: property.description }}
                />
              ) : (
                <p className="text-muted-foreground leading-relaxed">
                  Descrição não disponível.
                </p>
              )}
              {developmentData && (
                <div className="mt-4 p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    Caso tenha dúvidas sobre a localização deste apartamento, acesse a página{" "}
                    <Link
                      to={developmentHref(developmentData)}
                      className="text-primary hover:underline font-medium"
                    >
                      Sobre
                    </Link>
                    {" "}e veja o mapa do empreendimento.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-4">Amenidades</h2>
              <div className="grid grid-cols-2 gap-4">
                {(() => {
                  // Parse das comodidades (pode ser JSON array ou string separada por vírgulas)
                  let amenitiesArray: string[] = [];
                  try {
                    if (property.amenities) {
                      // Se já é um array, usar diretamente
                      if (Array.isArray(property.amenities)) {
                        amenitiesArray = property.amenities;
                      } else {
                        // Tentar fazer parse do JSON
                        amenitiesArray = JSON.parse(property.amenities);
                      }
                    }
                  } catch (error) {
                    // Se falhar o parse JSON, tentar como string separada por vírgulas
                    console.log('Tentando parse como string separada por vírgulas...');
                    if (typeof property.amenities === 'string') {
                      amenitiesArray = property.amenities.split(',').map(item => item.trim());
                    } else {
                      console.error('Erro ao fazer parse das comodidades:', error);
                      amenitiesArray = [];
                    }
                  }

                  if (amenitiesArray && amenitiesArray.length > 0) {
                    return amenitiesArray.map((amenity, index) => {
                      // Mapear comodidades para ícones
                      const getAmenityIcon = (amenityName: string) => {
                        const name = amenityName.toLowerCase();
                        if (name.includes('wifi') || name.includes('wi-fi')) return Wifi;
                        if (name.includes('tv')) return Tv;
                        if (name.includes('ar condicionado') || name.includes('ar-condicionado')) return Thermometer;
                        if (name.includes('piscina')) return Waves;
                        if (name.includes('estacionamento')) return ParkingCircle;
                        if (name.includes('cozinha')) return Utensils;
                        if (name.includes('academia') || name.includes('fitness')) return Dumbbell;
                        if (name.includes('restaurante')) return Utensils;
                        if (name.includes('sauna')) return Thermometer;
                        if (name.includes('praia')) return Waves;
                        if (name.includes('vista')) return Star;
                        if (name.includes('parque') || name.includes('infantil')) return Heart; // Para parque infantil
                        return Wifi; // Ícone padrão
                      };

                      const Icon = getAmenityIcon(amenity);
                      return (
                        <div key={index} className="flex items-center gap-3 text-foreground">
                          <Icon className="h-5 w-5 text-primary" />
                          {amenity}
                        </div>
                      );
                    });
                  } else {
                    return (
                      <div className="col-span-2 text-muted-foreground">
                        Nenhuma comodidade cadastrada.
                      </div>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Regras */}
            <div className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-4">Regras</h2>
              <div className="space-y-4">
                {/* Horários de Check-in/Check-out */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">IN</span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Check-in</p>
                      <p className="font-medium">{developmentData?.check_in_time || property.check_in_time || '14:00'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                      <span className="text-primary font-semibold text-sm">OUT</span>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Check-out</p>
                      <p className="font-medium">{developmentData?.check_out_time || property.check_out_time || '11:00'}</p>
                    </div>
                  </div>
                </div>

                {/* Regras da Casa */}
                {mergedHouseRules && (
                  <div>
                    <h3 className="font-medium mb-2">Regras e Políticas</h3>
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div 
                        className="text-sm text-foreground prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: mergedHouseRules }}
                      />
                    </div>
                  </div>
                )}

                {/* Informações Adicionais */}
                {mergedAdditionalInfo && (
                  <div>
                    <h3 className="font-medium mb-2">Informações Adicionais</h3>
                    <div className="bg-muted/30 rounded-lg p-4">
                      <div 
                        className="text-sm text-foreground prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: mergedAdditionalInfo }}
                      />
                    </div>
                  </div>
                )}

                {parseDevelopmentPhotos().length > 0 && (
                  <div>
                    <h3 className="font-medium mb-2">Fotos do Empreendimento</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {parseDevelopmentPhotos().map((photoPath: string) => (
                        <img
                          key={photoPath}
                          src={photoPath}
                          alt="Foto do empreendimento"
                          className="w-full h-24 rounded-lg object-cover border"
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>


          {/* Card de Contato */}
          <div className="lg:col-span-1">
            {/* Card de Datas Disponíveis */}
            {property && !saleMode && (
              <AvailableDatesCard
                propertyId={property.id}
                propertyMaxGuests={property.max_guests}
                isAuthenticated={isAuthenticated}
                onBookingClick={handleBooking}
              />
            )}

            <Card className="sticky top-24 shadow-medium">
              <CardContent className="p-6">
                {!isAuthenticated ? (
                  // Mensagem para usuários não logados
                  <div className="text-center space-y-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                      <Heart className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">
                        Faça seu login
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        Faça seu login e tenha acesso ao contato do Proprietário e garanta a sua melhor experiência
                      </p>
                    </div>
                    <Button 
                      className="w-full h-12"
                      onClick={() => navigate("/login")}
                    >
                      Fazer Login
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full h-12"
                      onClick={() => navigate("/register")}
                    >
                      Criar Conta
                    </Button>
                  </div>
                ) : (
                  // Card original para usuários logados
                  <>
                    <h3 className="text-xl font-semibold mb-4">
                      {saleMode
                        ? 'Contato'
                        : userType === 'admin'
                          ? 'Contato com Proprietário'
                          : 'Reservar e Contato'}
                    </h3>

                    {!isOwnerOfThisProperty && (
                      <div className="space-y-3">
                        {saleMode ? (
                          <Button
                            className="w-full h-12 gap-2"
                            onClick={handleContactOwner}
                            disabled={loading || bookingLoading}
                          >
                            <MessageCircle className="h-5 w-5" />
                            {loading ? "Carregando..." : "Falar com Proprietário"}
                          </Button>
                        ) : userType === 'admin' ? (
                          // Interface específica para usuários admin
                          <div className="space-y-3">
                            <Alert>
                              <AlertCircle className="h-4 w-4" />
                              <AlertDescription>
                                <strong>Usuário Administrador</strong>
                                <br />
                                Como administrador, você não pode fazer reservas. Use a opção abaixo para entrar em contato com o proprietário.
                              </AlertDescription>
                            </Alert>
                            <Button
                              className="w-full h-12 gap-2"
                              onClick={handleContactOwner}
                              disabled={loading || bookingLoading}
                            >
                              <MessageCircle className="h-5 w-5" />
                              {loading ? "Carregando..." : "Falar com Proprietário"}
                            </Button>
                          </div>
                        ) : (
                          // Interface normal para usuários comuns e proprietários
                          <>
                            {checkingAvailability ? (
                              <Button className="w-full h-12" disabled>
                                Verificando disponibilidade...
                              </Button>
                            ) : hasAvailablePeriods ? (
                              <Button 
                                className="w-full h-12"
                                onClick={handleBooking}
                                disabled={loading || bookingLoading}
                              >
                                {bookingLoading ? "Processando..." : "Reservar Agora"}
                              </Button>
                            ) : (
                              <Button className="w-full h-12" disabled variant="outline">
                                Sem períodos disponíveis
                              </Button>
                            )}

                            {!checkingAvailability && !hasAvailablePeriods && (
                              <Alert>
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription>
                                  <strong>Este imóvel não possui períodos disponíveis para locação no momento.</strong>
                                  <br />
                                  Entre em contato com o proprietário para mais informações sobre futuras disponibilidades.
                                </AlertDescription>
                              </Alert>
                            )}
                            
                            <Button
                              variant="outline"
                              className="w-full h-12 gap-2"
                              onClick={handleContactOwner}
                              disabled={loading || bookingLoading}
                            >
                              <MessageCircle className="h-5 w-5" />
                              {loading ? "Carregando..." : "Falar com Proprietário"}
                            </Button>
                          </>
                        )}
                      </div>
                    )}

                    {isOwnerOfThisProperty && (
                      <div className="space-y-3">
                        <Alert>
                          <AlertCircle className="h-4 w-4" />
                          <AlertDescription>
                            <strong>Este é seu próprio imóvel.</strong>
                            <br />
                            Você pode gerenciá-lo através da sua área de proprietário.
                          </AlertDescription>
                        </Alert>
                        <Button
                          variant="outline"
                          className="w-full h-12 gap-2"
                          onClick={() => navigate("/minha-area/meus-imoveis")}
                        >
                          <ArrowLeft className="h-5 w-5" />
                          Ir para Meus Imóveis
                        </Button>
                      </div>
                    )}

                    {/* Informações do Proprietário - apenas para usuários logados */}
                    {owner && (
                      <div className="mt-6 pt-6 border-t">
                        <h4 className="font-semibold mb-4">Proprietário</h4>
                        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="relative">
                            <img
                              src={
                                owner.profile_photo 
                                  ? getUserImageUrl(owner.id, owner.profile_photo)
                                  : '/placeholder.svg'
                              }
                              alt={`Foto de ${owner.name}`}
                              className="w-12 h-12 rounded-full object-cover border-2 border-primary/20"
                              onError={(e) => {
                                e.currentTarget.src = '/placeholder.svg';
                              }}
                            />
                            {loadingOwner && (
                              <div className="absolute inset-0 bg-background/50 rounded-full flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h5 className="font-semibold text-sm text-foreground truncate">
                              {owner.name}
                            </h5>
                            <p className="text-xs text-muted-foreground">
                              Proprietário desde {new Date(owner.created_at).toLocaleDateString('pt-BR', { 
                                year: 'numeric', 
                                month: 'short' 
                              })}
                            </p>
                            {canSeeOwnerContactDetails && owner.phone && (
                              <a
                                href={buildWhatsAppUrl(
                                  owner.phone,
                                  owner.phone_ddi,
                                  buildPropertyWhatsAppMessage(
                                    property?.title || 'Imóvel',
                                    property?.description,
                                  ),
                                )}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline mt-1 inline-flex items-center gap-1"
                                aria-label={`Falar com ${owner.name} no WhatsApp`}
                              >
                                📞 {owner.phone}
                              </a>
                            )}
                            {canSeeOwnerContactDetails && owner.email && (
                              <p className="text-xs text-muted-foreground mt-1">
                                ✉️ {owner.email}
                              </p>
                            )}
                            {!canSeeOwnerContactDetails && (
                              <p className="text-xs text-muted-foreground mt-2">
                                Para falar com o proprietário, use o botão{" "}
                                <strong>Falar com Proprietário</strong> (chat).
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Botão para buscar imóveis do proprietário - apenas para usuários logados */}
                        {isAuthenticated && owner && (
                          <div className="mt-4">
                            <Button
                              variant="outline"
                              className="w-full gap-2"
                              onClick={() => {
                                navigate(`/busca?ownerId=${owner.id}`);
                              }}
                            >
                              <Building2 className="h-4 w-4" />
                              Ver todos os imóveis deste proprietário
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

          </div>
        )}
      </div>

      {conversationId && (
        <Chat
          conversationId={conversationId}
          onClose={() => setConversationId(null)}
        />
      )}

      {property && (
        <BookingModal
          isOpen={showBookingModal}
          onClose={() => setShowBookingModal(false)}
          onConfirm={handleConfirmBooking}
          propertyTitle={property.title}
          propertyId={property.id}
          propertyDescription={property.description}
          propertyMaxGuests={property.max_guests}
          ownerName={owner?.name || "Proprietário"}
          ownerPhone={
            canSeeOwnerContactDetails ? owner?.phone || "" : ""
          }
          ownerPhoneDdi={
            canSeeOwnerContactDetails ? owner?.phone_ddi || "55" : "55"
          }
          ownerEmail={
            canSeeOwnerContactDetails ? owner?.email || "" : ""
          }
          showOwnerContact={canSeeOwnerContactDetails}
          loading={bookingLoading}
        />
      )}

      {/* Modal de Imagem em Tamanho Maior */}
      {property && (
        <Dialog open={showImageModal} onOpenChange={setShowImageModal}>
          <DialogContent className="max-w-[95vw] max-h-[95vh] w-[95vw] h-[95vh] p-0 gap-0 bg-black/95 border-none translate-x-[-50%] translate-y-[-50%] left-[50%] top-[50%] rounded-lg overflow-hidden">
            <DialogTitle className="sr-only">
              {propertyPhotos.length > 0 && propertyPhotos[currentImageIndex]?.caption 
                ? propertyPhotos[currentImageIndex].caption 
                : property.title || "Imóvel"
              }
            </DialogTitle>
            <div className="relative w-full h-full flex flex-col overflow-hidden">
              {/* Imagem Principal */}
              <div className="relative flex-1 flex items-center justify-center min-h-0 p-2 md:p-4 overflow-hidden">
                {propertyPhotos.length > 0 ? (
                  <img
                    src={getPropertyImageUrl(property.id, propertyPhotos[currentImageIndex]?.filename)}
                    alt={`${property.title} - Foto ${currentImageIndex + 1}`}
                    className="object-contain"
                    style={{ 
                      maxWidth: 'calc(95vw - 4rem)',
                      maxHeight: 'calc(95vh - 12rem)',
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                ) : (
                  <img
                    src={apartment1}
                    alt={property.title || "Imóvel"}
                    className="object-contain"
                    style={{ 
                      maxWidth: 'calc(95vw - 4rem)',
                      maxHeight: 'calc(95vh - 12rem)',
                      width: 'auto',
                      height: 'auto'
                    }}
                  />
                )}

                {/* Botões de Navegação - Apenas se houver mais de uma foto */}
                {propertyPhotos.length > 1 && (
                  <>
                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg z-10 bg-black/50 hover:bg-black/70 text-white border-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        prevImage();
                      }}
                    >
                      <ChevronLeft className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>

                    <Button
                      variant="secondary"
                      size="icon"
                      className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg z-10 bg-black/50 hover:bg-black/70 text-white border-none"
                      onClick={(e) => {
                        e.stopPropagation();
                        nextImage();
                      }}
                    >
                      <ChevronRight className="h-5 w-5 md:h-6 md:w-6" />
                    </Button>
                  </>
                )}

                {/* Contador de Imagens */}
                {propertyPhotos.length > 1 && (
                  <div className="absolute top-2 md:top-4 right-2 md:right-4 bg-black/70 text-white px-2 md:px-3 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium backdrop-blur-sm z-10">
                    {currentImageIndex + 1} / {propertyPhotos.length}
                  </div>
                )}
              </div>

              {/* Legenda e Controles Inferiores */}
              <div className="bg-black/80 text-white px-3 md:px-4 py-2 md:py-3 backdrop-blur-sm flex-shrink-0 min-h-[4rem]">
                <p className="text-xs md:text-sm lg:text-base text-center break-words">
                  {propertyPhotos.length > 0 && propertyPhotos[currentImageIndex]?.caption 
                    ? propertyPhotos[currentImageIndex].caption 
                    : property.title || "Imóvel"
                  }
                </p>
                {/* Paginação - apenas se houver mais de uma foto */}
                {propertyPhotos.length > 1 && (
                  <div className="flex justify-center gap-2 mt-2 md:mt-3 max-w-full overflow-x-auto scrollbar-hide pb-1">
                    {propertyPhotos.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentImageIndex(index)}
                        className={`h-1.5 md:h-2 rounded-full transition-all flex-shrink-0 w-6 md:w-8 ${
                          index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <Footer />
    </div>
  );
};

export default PropertyDetails;